import {
  Button,
  Form,
  Modal,
  notification,
  Popconfirm,
  Space,
  Spin,
  Typography,
} from "antd";
import {
  ClarificationComplementModel,
  ComplementModel,
  multiAssignComplement,
} from "api/generated";
import { COMPLEMENT_PARENT } from "routes/cfdiComplements/ComplementsScreen";
import { Key, useState } from "react";
import { useClient } from "hooks/useClient";
import { ClarificationComplementForm } from "routes/cfdiComplements/subComplement/ClarificationComplementForm";
import { handleAxiosError } from "common/helpers/generalUtils";

interface AssignClarificationProps {
  onCancel: () => void;
  onSuccess: () => void;
  selectedKeys: Key[];
  parentType: COMPLEMENT_PARENT;
}

function AssignClarification({
  onCancel,
  onSuccess,
  selectedKeys,
  parentType,
}: AssignClarificationProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { client } = useClient();
  const [complementData] = Form.useForm<ComplementModel>();
  const clarification: ClarificationComplementModel | undefined = Form.useWatch(
    "clarification",
    { form: complementData, preserve: true }
  );

  function handleOnConfirm() {
    complementData
      .validateFields()
      .then((values) => {
        if (!values.clarification?.description || selectedKeys.length === 0) {
          return;
        }
        setIsLoading(true);
        multiAssignComplement({
          client,
          body: {
            movementIds: selectedKeys.map((key) => `${key}`),
            parentType,
            type: "CLARIFICATION",
            clarification: values.clarification.description,
          },
        })
          .then(({ data }) => {
            notification.success({
              message: "Asignación exitosa",
              description: `${data}`,
            });
            onSuccess();
          })
          .catch((err) => handleAxiosError(err, "Error asignando Aclaración"))
          .finally(() => {
            setIsLoading(false);
          });
      })
      .catch((err) => {
        console.error(err);
        notification.error({
          message: "Error al asignar la Aclaración",
          description: `Revisa que los datos que ingreaste sean validos`,
        });
      });
  }

  function getParentTypeName() {
    switch (parentType) {
      case COMPLEMENT_PARENT.RECEPCION:
        return `${parentType.toLowerCase()}es`;
      case COMPLEMENT_PARENT.VENTA:
      default:
        return `${parentType.toLowerCase()}s`;
    }
  }

  const DoubleConfirmation = (
    <Popconfirm
      title={`Asignación de Aclaración a ${getParentTypeName()}`}
      description={
        <div
          style={{
            padding: "1rem",
            textAlign: "center",
          }}
        >
          <Typography.Text>
            ¿Está seguro de realizar la asignación de la aclaración a{" "}
            {selectedKeys.length} {getParentTypeName()}?
          </Typography.Text>
        </div>
      }
      onConfirm={handleOnConfirm}
      okText="Sí"
      cancelText="No"
    >
      <Button
        key="submit"
        size="large"
        type="primary"
        disabled={!clarification?.description}
      >
        Asignar
      </Button>
    </Popconfirm>
  );

  return (
    <Modal
      onCancel={() => {
        onCancel();
      }}
      open={true}
      styles={{
        mask: {
          backdropFilter: "blur(10px)",
        },
      }}
      width="100%"
      height="100%"
      title={`Asignación de CFDI a ${
        selectedKeys.length
      } ${getParentTypeName()}`}
      footer={
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button key="back" onClick={onCancel} size="large">
            Cancelar
          </Button>

          {DoubleConfirmation}
        </Space>
      }
    >
      <Spin spinning={isLoading}>
        <Form layout="vertical" form={complementData}>
          <ClarificationComplementForm />
        </Form>
      </Spin>
    </Modal>
  );
}

export default AssignClarification;
